import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GoogleMap, useJsApiLoader, Marker, Autocomplete, type Libraries } from '@react-google-maps/api'
import { useQuery } from '@tanstack/react-query'
import { Search, MapPin, LocateFixed, Link2 } from 'lucide-react'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { GOOGLE_MAPS_API_KEY } from '@/shared/constants'
import { toast } from '@/store/useToastStore'
import { mapPinIcon } from '@/shared/ui/mapPinIcon'

// Students/trainers often send their location as a Google Maps link rather than
// an address. A full link embeds the coordinates in one of a few well-known
// spots — tried in order of precision (the exact pinned place beats the map's
// viewport center, which is all a plain @lat,lng carries). A *shortened* link
// (goo.gl/maps/…, maps.app.goo.gl/…) has no coordinates in the URL itself —
// resolving it requires following its redirect, which a browser can't do
// cross-origin, so those are reported back as unsupported rather than silently
// failing to match.
const SHORT_LINK_HOSTS = ['goo.gl', 'maps.app.goo.gl', 'g.co']
function parseGoogleMapsLink(raw: string): { lat: number; lng: number } | null | 'short' {
  const input = raw.trim()
  if (!input) return null

  try {
    const url = new URL(input)
    if (SHORT_LINK_HOSTS.some(h => url.hostname === h || url.hostname.endsWith(`.${h}`))) return 'short'
  } catch {
    // Not a URL at all (e.g. a bare "24.45,54.38") — fall through to the coordinate patterns below.
  }

  const patterns = [
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,     // the actual pinned place, embedded in /maps/place/… links
    /[?&]q=(-?\d+\.\d+),\s*(-?\d+\.\d+)/, // ?q=lat,lng
    /[?&]ll=(-?\d+\.\d+),\s*(-?\d+\.\d+)/,// ?ll=lat,lng
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,         // @lat,lng,zoom — the map's viewport center
    /^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/,     // a bare "lat,lng" pasted directly
  ]
  for (const re of patterns) {
    const m = input.match(re)
    if (m) {
      const lat = Number(m[1]), lng = Number(m[2])
      if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng }
    }
  }
  return null
}

const LIBRARIES: Libraries = ['places']
export const ADDRESS_DEFAULT_CENTER = { lat: 24.4539, lng: 54.3773 } // Abu Dhabi

const enName = (translations: any[], fallbackKey = 'name') =>
  translations?.find((tr: any) => tr?.locale?.toLowerCase() === 'en')?.[fallbackKey] ??
  translations?.[0]?.[fallbackKey]

const stateName = (s: any) =>
  enName(s.translations) ?? enName(s.state_translations) ?? enName(s.state_province_translations) ?? s.name

const normalize = (s: string) => s.trim().toLowerCase()

// Best-effort match: Google's place name for a city/area rarely lines up
// character-for-character with how it's spelled in our own list, so an exact
// hit falls back to "either name contains the other" before giving up.
function matchByName<T>(list: T[], name: string | null, getName: (item: T) => string | undefined): T | null {
  if (!name) return null
  const target = normalize(name)
  return list.find(item => {
    const n = getName(item)
    if (!n) return false
    const norm = normalize(n)
    return norm === target || norm.includes(target) || target.includes(norm)
  }) ?? null
}

// Google's UAE results put the emirate in administrative_area_level_1 and the
// district/area in sublocality — that maps neatly onto our city / area split.
function extractLocationParts(components?: google.maps.GeocoderAddressComponent[] | google.maps.places.PlaceResult['address_components']) {
  const find = (type: string) => components?.find(c => c.types.includes(type))?.long_name ?? null
  return {
    cityName: find('administrative_area_level_1') ?? find('locality'),
    areaName: find('sublocality_level_1') ?? find('sublocality') ?? find('neighborhood'),
    postalCode: find('postal_code'),
  }
}

// Field shape shared by the modal and the inline create-page section.
export type AddressValue = {
  label?: string
  building_name?: string
  plot_number?: string
  street?: string
  landmark?: string
  phone_number?: string
  country_code?: string
  city_id?: number | string | ''
  state_province_id?: number | string | ''
  postal_code?: string
  additional_notes?: string
  neighbourhood?: string
  is_default?: boolean
}

export const EMPTY_ADDRESS: AddressValue = {
  label: '', building_name: '', plot_number: '', street: '', landmark: '',
  phone_number: '', country_code: '971', city_id: '', state_province_id: '',
  postal_code: '', additional_notes: '', neighbourhood: '', is_default: false,
}

type Props = {
  value: AddressValue
  onChange: (patch: Partial<AddressValue>) => void
  pos: { lat: number; lng: number }
  onPosChange: (p: { lat: number; lng: number }) => void
  // Gates the city/state dropdown queries so they don't run while hidden.
  active?: boolean
  // Hide the "Set as default address" checkbox (e.g. a new trainer's first
  // address is always the default, so there's nothing to ask).
  hideDefaultToggle?: boolean
}

/**
 * Controlled address picker: Google map (search / locate-me / drag pin) plus the
 * address fields. It only reads/writes `value` + `pos` — persistence is the
 * caller's job (AddressModal saves immediately; the create page saves after the
 * trainer is created and its user id exists).
 */
export function AddressPicker({ value, onChange, pos, onPosChange, active = true, hideDefaultToggle = false }: Props) {
  const { t } = useTranslation()
  const { isLoaded } = useJsApiLoader({
    id: 'drivys-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  })

  const acRef = useRef<google.maps.places.Autocomplete | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  // Bumped on every placement so the Marker remounts and its DROP animation replays.
  const markerKeyRef = useRef(0)
  const [mapsLink, setMapsLink] = useState('')
  // The area/state list only loads once a city is known, so a location that also
  // implies an area is applied in two steps: set the city now, remember the area
  // name here, then the effect below finishes the match once that city's areas load.
  const pendingAreaName = useRef<string | null>(null)

  const set = (k: keyof AddressValue, v: any) => onChange({ [k]: v })

  // Admin city list (not the public one, which drops unpublished cities).
  const { data: citiesRaw } = useQuery({
    queryKey: ['cities-list'],
    queryFn: () => restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r => r.data),
    staleTime: 600_000,
    enabled: active,
  })
  const cities: any[] = (citiesRaw as any)?.data ?? []

  const { data: statesRaw } = useQuery({
    queryKey: ['states-list', value.city_id],
    queryFn: () => restClient.get(`${endpoints.users.listAreas}?city_id=${value.city_id}&limit=500`).then(r => r.data),
    enabled: active && !!value.city_id,
  })
  const states: any[] = (statesRaw as any)?.data ?? []

  // Once the newly-selected city's areas arrive, resolve whatever area name the
  // last geocode reported against them.
  useEffect(() => {
    if (!pendingAreaName.current || !states.length) return
    const match = matchByName(states, pendingAreaName.current, stateName)
    if (match) set('state_province_id', match.id)
    pendingAreaName.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states])

  // Fills in the fields a geocoded location implies — street text, postal code,
  // neighbourhood, and (best-effort, matched by name) the city/area dropdowns —
  // so picking a spot on the map or from search doesn't leave everything below
  // it for the admin to type out by hand.
  const applyLocationDetails = (
    formattedAddress: string | null | undefined,
    components: google.maps.GeocoderAddressComponent[] | google.maps.places.PlaceResult['address_components'],
  ) => {
    const { cityName, areaName, postalCode } = extractLocationParts(components)
    const patch: Partial<AddressValue> = {}
    if (formattedAddress) patch.street = formattedAddress
    if (postalCode) patch.postal_code = postalCode
    if (areaName) patch.neighbourhood = areaName

    const cityMatch = matchByName(cities, cityName, c => enName(c.city_translations) ?? c.name)
    if (cityMatch && cityMatch.id !== value.city_id) {
      patch.city_id = cityMatch.id
      patch.state_province_id = ''
      pendingAreaName.current = areaName
    } else if (cityMatch) {
      // Same city as before — its areas are already loaded, so match right away.
      const areaMatch = matchByName(states, areaName, stateName)
      if (areaMatch) patch.state_province_id = areaMatch.id
    }

    onChange(patch)
  }

  const reverseGeocode = (next: google.maps.LatLngLiteral) => {
    if (!geocoderRef.current) geocoderRef.current = new google.maps.Geocoder()
    geocoderRef.current.geocode({ location: next }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        applyLocationDetails(results[0].formatted_address, results[0].address_components)
      }
    })
  }

  const onPlaceChanged = () => {
    const place = acRef.current?.getPlace()
    const loc = place?.geometry?.location
    if (!loc) {
      toast.error(t('addressPicker.pickSuggestion'))
      return
    }
    const next = { lat: loc.lat(), lng: loc.lng() }
    flyTo(next)
    applyLocationDetails(place.formatted_address ?? value.street, place.address_components)
  }

  function flyTo(next: google.maps.LatLngLiteral, zoom = 16) {
    markerKeyRef.current += 1
    onPosChange(next)
    mapRef.current?.panTo(next)
    mapRef.current?.setZoom(zoom)
  }

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return
    const next = { lat: e.latLng.lat(), lng: e.latLng.lng() }
    markerKeyRef.current += 1
    onPosChange(next)
    reverseGeocode(next)
  }

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error(t('addressPicker.locationNotAvailable')); return }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = { lat: position.coords.latitude, lng: position.coords.longitude }
        flyTo(next, 17)
        reverseGeocode(next)
      },
      () => toast.error(t('addressPicker.locationError')),
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  const useMapsLink = (e: React.FormEvent) => {
    e.preventDefault()
    const result = parseGoogleMapsLink(mapsLink)
    if (result === 'short') { toast.error(t('addressPicker.mapsLinkShortened')); return }
    if (!result) { toast.error(t('addressPicker.mapsLinkInvalid')); return }
    flyTo(result, 17)
    reverseGeocode(result)
    setMapsLink('')
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400'

  return (
    <div className="space-y-4">
      {/* Location search */}
      <div>
        <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.searchLocation')}</label>
        {isLoaded ? (
          <div className="flex items-center gap-2">
            <Autocomplete
              onLoad={ac => (acRef.current = ac)}
              onPlaceChanged={onPlaceChanged}
              options={{ componentRestrictions: { country: 'ae' }, fields: ['geometry', 'formatted_address', 'address_components'] }}
            >
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-brand-400 flex-1">
                <Search size={14} className="text-gray-400 flex-shrink-0" />
                <input placeholder={t('addressPicker.searchPlaceholder')} className="text-sm flex-1 focus:outline-none" />
              </div>
            </Autocomplete>
            <button
              type="button"
              onClick={locateMe}
              title={t('addressPicker.locateMeTitle')}
              className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 flex-shrink-0"
            >
              <LocateFixed size={14} />
              <span className="hidden sm:inline">{t('addressPicker.locateMe')}</span>
            </button>
          </div>
        ) : (
          <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />
        )}
      </div>

      {/* Paste a Google Maps link — how students/trainers usually share where they are */}
      <form onSubmit={useMapsLink}>
        <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.mapsLinkLabel')}</label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-brand-400 flex-1">
            <Link2 size={14} className="text-gray-400 flex-shrink-0" />
            <input
              value={mapsLink}
              onChange={e => setMapsLink(e.target.value)}
              placeholder={t('addressPicker.mapsLinkPlaceholder')}
              className="text-sm flex-1 focus:outline-none min-w-0"
            />
          </div>
          <button
            type="submit"
            disabled={!mapsLink.trim()}
            className="flex-shrink-0 border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            {t('addressPicker.mapsLinkUse')}
          </button>
        </div>
      </form>

      {/* Map */}
      {GOOGLE_MAPS_API_KEY && isLoaded ? (
        <div className="rounded-lg overflow-hidden border border-gray-100">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: 240 }}
            center={pos}
            zoom={15}
            onClick={onMapClick}
            onLoad={m => { mapRef.current = m }}
            onUnmount={() => { mapRef.current = null }}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
          >
            <Marker
              key={markerKeyRef.current}
              position={pos}
              draggable
              animation={google.maps.Animation.DROP}
              icon={mapPinIcon()}
              onDragEnd={(e) => {
                if (!e.latLng) return
                const next = { lat: e.latLng.lat(), lng: e.latLng.lng() }
                onPosChange(next)
                reverseGeocode(next)
              }}
            />
          </GoogleMap>
        </div>
      ) : (
        <div className="rounded-lg bg-gray-100 animate-pulse flex items-center justify-center text-xs text-gray-400" style={{ height: 240 }}>
          {GOOGLE_MAPS_API_KEY ? t('addressPicker.loadingMap') : t('addressPicker.mapUnavailable')}
        </div>
      )}
      <p className="text-[13px] text-gray-400 flex items-center gap-1 -mt-2"><MapPin size={11} /> {t('addressPicker.mapHint')}</p>

      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.label')}</label>
          <input value={value.label ?? ''} onChange={e => set('label', e.target.value)} placeholder={t('addressPicker.labelPlaceholder')} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.phoneNumber')}</label>
          <input value={value.phone_number ?? ''} onChange={e => set('phone_number', e.target.value)} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.streetAddress')}</label>
          <input value={value.street ?? ''} onChange={e => set('street', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.buildingVillaNo')}</label>
          <input value={value.building_name ?? ''} onChange={e => set('building_name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.plotNumber')}</label>
          <input value={value.plot_number ?? ''} onChange={e => set('plot_number', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.city')}</label>
          <select value={value.city_id ?? ''} onChange={e => { onChange({ city_id: e.target.value ? Number(e.target.value) : '', state_province_id: '' }) }} className={inputCls}>
            <option value="">{t('addressPicker.selectCity')}</option>
            {cities.map((c: any) => <option key={c.id} value={c.id}>{enName(c.city_translations) ?? c.name ?? `City ${c.id}`}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.areaState')}</label>
          <select value={value.state_province_id ?? ''} onChange={e => set('state_province_id', e.target.value ? Number(e.target.value) : '')} disabled={!value.city_id} className={inputCls}>
            <option value="">{value.city_id ? t('addressPicker.selectArea') : t('addressPicker.selectCityFirst')}</option>
            {states.map((s: any) => (
              <option key={s.id} value={s.id}>
                {enName(s.translations) ?? enName(s.state_translations) ?? enName(s.state_province_translations) ?? s.name ?? `Area ${s.id}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.landmark')}</label>
          <input value={value.landmark ?? ''} onChange={e => set('landmark', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.neighbourhood')}</label>
          <input value={value.neighbourhood ?? ''} onChange={e => set('neighbourhood', e.target.value)} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 block mb-1">{t('addressPicker.additionalNotes')}</label>
          <input value={value.additional_notes ?? ''} onChange={e => set('additional_notes', e.target.value)} className={inputCls} />
        </div>
        {!hideDefaultToggle && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!value.is_default} onChange={e => set('is_default', e.target.checked)} className="w-4 h-4 accent-brand-500" />
            <span className="text-sm text-gray-700">{t('addressPicker.setAsDefault')}</span>
          </label>
        )}
      </div>
    </div>
  )
}
